/* Twee aanvullingen op het formulier van Websitecontent (ADR 0024): de kaart "In het kort" van een
 * oplossing of dienst, en de plaats van een projectverhaal voor de kaart op de pagina Projecten.
 * Dit bestand tekent alleen. De regels staan in data/kerngegevens.ts en data/plaats.ts.
 *
 * Beide horen bij kolommen die er pas zijn als de eigenaar van de database een script heeft gedraaid.
 * Heeft de rij de kolom niet, dan staat hier welk script dat is, en wordt er niets van geschreven.
 * Bij iets nieuws is er nog geen rij: dan komt het onderdeel na de eerste keer opslaan. */

import { RotateCcw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  KERNVRAGEN, LABEL as IN_HET_KORT, SCRIPT as SCRIPT_KERNGEGEVENS, heeftKerngegevens, leesKerngegevens, teSchrijven, zetAntwoord,
} from './data/kerngegevens';
import {
  BREEDTE, GRENZEN, KOLOMMEN, LENGTE, PLAATS, SCRIPT as SCRIPT_PLAATS, STIP_ZIN, graadInvoer, heeftPlaats, leesGraad, schrijfPlaats, stipStaat, toonGraad,
} from './data/plaats';
import type { Rij } from './data/website';

/* De kaart als er niets te bewerken valt: de kolom is er nog niet, of de rij is er nog niet. */
export function Wacht({ kaart, titel, staat, etiket, children }: { kaart: string; titel: string; staat: 'niet-beschikbaar' | 'na-opslaan'; etiket: string; children: React.ReactNode }) {
  return (
    <Card data-kaart={kaart} data-kaart-staat={staat}>
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">{titel} <Badge variant="secondary">{etiket}</Badge></CardTitle>
      </CardHeader>
      <CardContent><p className="text-sm text-muted-foreground">{children}</p></CardContent>
    </Card>
  );
}

export function NietBeschikbaar({ kaart, titel, script }: { kaart: string; titel: string; script: string }) {
  return (
    <Wacht kaart={kaart} titel={titel} staat="niet-beschikbaar" etiket="Nog niet beschikbaar">
      Dit onderdeel is nog niet beschikbaar. De eigenaar van de database draait eerst het script <code className="break-all text-xs">{script}</code> uit
      de repo van de site (greenvision-site). Tot die tijd wordt hier niets van opgeslagen.
    </Wacht>
  );
}

export function NaOpslaan({ kaart, titel }: { kaart: string; titel: string }) {
  return (
    <Wacht kaart={kaart} titel={titel} staat="na-opslaan" etiket="Na het opslaan">
      Dit vul je in nadat je voor het eerst hebt opgeslagen.
    </Wacht>
  );
}

/** Het etiket bij een onderdeel dat anders is dan in de database, met de knop om het terug te zetten. */
export function Gewijzigd({ uit, terug }: { uit: boolean; terug: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant="secondary">Gewijzigd, nog niet opgeslagen</Badge>
      <Button variant="ghost" size="sm" disabled={uit} onClick={terug}><RotateCcw /> Terugzetten</Button>
    </div>
  );
}

/* ── In het kort ───────────────────────────────────────────────────────────── */

/** `vorige` is de rij in de database (bij iets nieuws niets), `waarde` het object in het concept. Is
 *  geen antwoord anders dan in de database, dan wordt de kolom bij opslaan niet geschreven. */
export function InHetKortKaart({ vorige, waarde, fout, uit, zet }: { vorige: Rij | null; waarde: unknown; fout?: string; uit: boolean; zet: (waarde: unknown) => void }) {
  const kaart = 'key_facts';
  if (!vorige) return <NaOpslaan kaart={kaart} titel={IN_HET_KORT} />;
  if (!heeftKerngegevens(vorige)) return <NietBeschikbaar kaart={kaart} titel={IN_HET_KORT} script={SCRIPT_KERNGEGEVENS} />;

  const opgeslagen = vorige.key_facts;
  const basis = leesKerngegevens(opgeslagen);
  const lezing = basis.ok ? leesKerngegevens(waarde) : basis;
  if (!lezing.ok) {
    return (
      <Card data-kaart={kaart} data-kaart-staat="alleen-lezen">
        <CardHeader className="pb-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">{IN_HET_KORT} <Badge variant="secondary">Alleen lezen</Badge></CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Deze kaart heeft een vorm die de bewerker niet kent. {lezing.reden} Daarom is hij hier niet te wijzigen. Hij blijft bij opslaan ongewijzigd.
          </p>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md border p-3 text-xs text-muted-foreground">{JSON.stringify(opgeslagen ?? null, null, 2)}</pre>
        </CardContent>
      </Card>
    );
  }

  const gewijzigd = teSchrijven(opgeslagen, waarde) !== null || !!fout;
  return (
    <Card data-kaart={kaart} data-kaart-staat={gewijzigd ? 'gewijzigd' : 'ongewijzigd'}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">{IN_HET_KORT}</CardTitle>
          {gewijzigd && <Gewijzigd uit={uit} terug={() => zet(opgeslagen)} />}
        </div>
        <p className="text-sm text-muted-foreground">
          De kaart met vier vaste vragen, bovenaan de pagina. Alleen een vraag met een antwoord staat op de site. Zonder antwoorden is er geen kaart.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <ol className="grid gap-3 lg:grid-cols-2">
          {KERNVRAGEN.map(({ sleutel, vraag }) => {
            const antwoord = lezing.antwoorden[sleutel];
            const id = `${kaart}-${sleutel}`;
            return (
              <li key={sleutel} className="space-y-3 rounded-md border p-3" data-vraag={sleutel}>
                <p className="text-sm font-medium">{vraag}</p>
                <div className="space-y-1.5">
                  <Label htmlFor={`${id}-antwoord`}>Antwoord</Label>
                  <Input id={`${id}-antwoord`} value={antwoord.tekst} disabled={uit} onChange={(e) => zet(zetAntwoord(waarde, sleutel, 'tekst', e.target.value))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`${id}-toelichting`}>Toelichting (mag leeg blijven)</Label>
                  <Input id={`${id}-toelichting`} value={antwoord.toelichting} disabled={uit} onChange={(e) => zet(zetAntwoord(waarde, sleutel, 'toelichting', e.target.value))} />
                </div>
              </li>
            );
          })}
        </ol>
        <p className="text-xs text-muted-foreground">
          Houd het antwoord kort: een paar woorden, of een getal met de eenheid erbij. De toelichting is één regel. Gewone tekst, geen HTML. Maak je een antwoord leeg, dan verdwijnt die vraag van de site.
        </p>
        {fout && <p className="text-xs text-destructive" role="alert">{fout}</p>}
      </CardContent>
    </Card>
  );
}

/* ── De plaats van een projectverhaal ──────────────────────────────────────── */

const PLAATS_TITEL = 'Plaats op de projectkaart';

function Graadvak({ kolom, concept, fout, uit, zet }: { kolom: typeof BREEDTE | typeof LENGTE; concept: Rij; fout?: string; uit: boolean; zet: (kolom: string, waarde: unknown) => void }) {
  const { naam } = GRENZEN[kolom];
  const graad = leesGraad(concept[kolom]);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={`veld-${kolom}`}>{naam[0].toUpperCase() + naam.slice(1)}</Label>
      <Input id={`veld-${kolom}`} inputMode="decimal" placeholder={toonGraad(kolom === BREEDTE ? 52.27 : 4.46)} value={graadInvoer(concept[kolom])} disabled={uit} onChange={(e) => zet(kolom, e.target.value)} />
      {graad.soort === 'getal' && graad.afgerond && !fout && (
        <p className="text-xs text-muted-foreground" data-afgerond={kolom}>Wordt opgeslagen als {toonGraad(graad.getal)}: twee cijfers achter de komma.</p>
      )}
      {fout && <p className="text-xs text-destructive" role="alert">{fout}</p>}
    </div>
  );
}

/** `vorige` is de rij in de database (bij iets nieuws niets). Een kolom die niemand aanraakt, wordt
 *  bij opslaan niet geschreven. */
export function PlaatsKaart({ vorige, concept, fouten, uit, zet }: { vorige: Rij | null; concept: Rij; fouten: Record<string, string>; uit: boolean; zet: (kolom: string, waarde: unknown) => void }) {
  const kaart = 'plaats';
  if (!vorige) return <NaOpslaan kaart={kaart} titel={PLAATS_TITEL} />;
  if (!heeftPlaats(vorige)) return <NietBeschikbaar kaart={kaart} titel={PLAATS_TITEL} script={SCRIPT_PLAATS} />;

  const gewijzigd = Object.keys(schrijfPlaats(vorige, concept)).length > 0 || KOLOMMEN.some((k) => fouten[k]);
  const staat = stipStaat(concept);
  const naam = typeof concept[PLAATS] === 'string' ? concept[PLAATS] : '';
  const terug = () => { for (const kolom of KOLOMMEN) zet(kolom, vorige[kolom]); };
  return (
    <Card data-kaart={kaart} data-kaart-staat={gewijzigd ? 'gewijzigd' : 'ongewijzigd'}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">{PLAATS_TITEL}</CardTitle>
          {gewijzigd && <Gewijzigd uit={uit} terug={terug} />}
        </div>
        <p className="text-sm text-muted-foreground">
          Voor de kaart van Nederland op de pagina Projecten: een stip per project. Vul het midden van de plaats in, nooit het adres van de klant.
          Noemt het verhaal geen plaats, of gaat het om een woonhuis, laat de drie vakken dan leeg.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor={`veld-${PLAATS}`}>Plaatsnaam</Label>
            <Input id={`veld-${PLAATS}`} placeholder="Noordwijk" value={naam} disabled={uit} onChange={(e) => zet(PLAATS, e.target.value)} />
            {/\d/.test(naam) && (
              <p className="text-xs text-amber-600" data-plaats-melding="cijfer">Er staat een cijfer in de plaatsnaam. Vul alleen de plaats in, geen adres of postcode.</p>
            )}
          </div>
          <Graadvak kolom={BREEDTE} concept={concept} fout={fouten[BREEDTE]} uit={uit} zet={zet} />
          <Graadvak kolom={LENGTE} concept={concept} fout={fouten[LENGTE]} uit={uit} zet={zet} />
        </div>
        <p className="text-xs text-muted-foreground">
          De twee getallen zijn de breedtegraad en de lengtegraad van het midden van de plaats, met twee cijfers achter de komma. Dat is ongeveer een kilometer:
          fijner slaat de CRM niet op. In Nederland ligt de breedtegraad tussen {toonGraad(GRENZEN.latitude.min)} en {toonGraad(GRENZEN.latitude.max)} en
          de lengtegraad tussen {toonGraad(GRENZEN.longitude.min)} en {toonGraad(GRENZEN.longitude.max)}. Vul ze allebei in, of geen van beide.
        </p>
        <p className="text-sm" data-stip={staat}>{STIP_ZIN[staat]}</p>
      </CardContent>
    </Card>
  );
}
